import { REWIND_BEATS, REWIND_TIMELINE_BEATS } from "./home-rewind.constants";
import type {
  RewindBeat,
  RewindBeatId,
  RewindBox,
  RewindTagId,
  RewindTrack,
} from "./home-rewind.types";

export const clamp01 = (value: number) => Math.min(1, Math.max(0, value));

export const lerp = (from: number, to: number, amount: number) =>
  from + (to - from) * amount;

const mod = (value: number, divisor: number) =>
  ((value % divisor) + divisor) % divisor;

export const getBeatAt = (progress: number): RewindBeat => {
  const clamped = clamp01(progress);
  return (
    REWIND_BEATS.find((beat) => clamped >= beat.start && clamped < beat.end) ??
    REWIND_BEATS[REWIND_BEATS.length - 1]
  );
};

export const getBeatById = (id: RewindBeatId): RewindBeat =>
  REWIND_BEATS.find((beat) => beat.id === id) ?? REWIND_BEATS[0];

/** 0 to 1 progress inside one beat. */
export const getBeatLocalProgress = (progress: number, beat: RewindBeat) =>
  clamp01((progress - beat.start) / (beat.end - beat.start));

/** Playhead position on the timeline bar, which covers every beat but the reveal. */
export const getTimelinePlayhead = (progress: number) => {
  const end = REWIND_TIMELINE_BEATS[REWIND_TIMELINE_BEATS.length - 1].end;
  return clamp01(progress / end);
};

/**
 * Frame shown during the rewind beat. It starts on the frame the intro was
 * showing and runs straight back to frame 0, the first frame of the take. One
 * short reverse, never a second viewing of the scene.
 */
export const getRewindFrame = (rewindProgress: number, anchorFrame: number) =>
  anchorFrame * (1 - clamp01(rewindProgress));

/**
 * Intro playback: the shot plays forward once from frame 0 and holds on
 * `holdTime` (normalized 0 to 1). It never loops, so nobody sees people
 * walk in twice.
 */
export const getIntroTime = (
  elapsedSeconds: number,
  shotSeconds: number,
  holdTime: number,
) => Math.min(holdTime, Math.max(0, elapsedSeconds / shotSeconds));

/** SMPTE-style timecode at 24 fps: HH:MM:SS:FF. */
export const formatTimecode = (totalFrames: number, fps = 24) => {
  const frames = Math.max(0, Math.floor(totalFrames));
  const pad = (value: number) => String(value).padStart(2, "0");
  const ff = frames % fps;
  const totalSeconds = Math.floor(frames / fps);
  const ss = totalSeconds % 60;
  const mm = Math.floor(totalSeconds / 60) % 60;
  const hh = Math.floor(totalSeconds / 3600);
  return `${pad(hh)}:${pad(mm)}:${pad(ss)}:${pad(ff)}`;
};

/** Source frame index (sampled at playbackFps) to 24 fps timecode frames. */
export const frameToTimecodeFrames = (frame: number, playbackFps: number) =>
  (frame / playbackFps) * 24;

/** Linear interpolation of tracked boxes between the surrounding keyframes. */
export const interpolateTrack = (
  track: RewindTrack,
  frame: number,
): Partial<Record<RewindTagId, RewindBox>> => {
  const keyframes = [...track.keyframes].sort((a, b) => a.frame - b.frame);
  if (keyframes.length === 0) return {};
  if (frame <= keyframes[0].frame) return keyframes[0].boxes;
  const last = keyframes[keyframes.length - 1];
  if (frame >= last.frame) return last.boxes;

  const nextIndex = keyframes.findIndex((keyframe) => keyframe.frame > frame);
  const previous = keyframes[nextIndex - 1];
  const next = keyframes[nextIndex];
  const amount = (frame - previous.frame) / (next.frame - previous.frame);

  const boxes: Partial<Record<RewindTagId, RewindBox>> = {};
  for (const id of Object.keys(previous.boxes) as RewindTagId[]) {
    const from = previous.boxes[id];
    const to = next.boxes[id];
    if (!from) continue;
    boxes[id] = to
      ? (from.map((value, index) =>
          lerp(value, to[index], amount),
        ) as RewindBox)
      : from;
  }
  return boxes;
};

export type RewindLeaderGeometry = {
  /** Box center and chip anchor, in % of the frame. */
  centerX: number;
  centerY: number;
  chipX: number;
  chipY: number;
  /** Leader length in % of the frame width, and its angle in degrees. */
  length: number;
  angle: number;
};

/** Leader line from a tracked box to its chip, in frame percentages. */
export const getLeaderGeometry = (
  box: RewindBox,
  offset: [number, number],
  aspect: number,
): RewindLeaderGeometry => {
  const centerX = box[0] + box[2] / 2;
  const centerY = box[1] + box[3] / 2;
  const [dx, dy] = offset;
  // Work in frame-width units so the angle holds at any size.
  const dyInWidth = dy / aspect;
  return {
    centerX: centerX * 100,
    centerY: centerY * 100,
    chipX: (centerX + dx) * 100,
    chipY: (centerY + dy) * 100,
    length: Math.hypot(dx, dyInWidth) * 100,
    angle: (Math.atan2(dyInWidth, dx) * 180) / Math.PI,
  };
};

/**
 * Odometer roll for one digit place. Returns 0 to 10, where 10 is the wrapped 0.
 * A digit holds still and only rolls during the last tenth of its carry, like a
 * mechanical counter.
 */
export const getOdometerDigitOffset = (value: number, place: number) => {
  const scaled = Math.max(0, value) / 10 ** place;
  const digit = Math.floor(scaled) % 10;
  if (place === 0) return mod(scaled, 10);
  const lower = scaled - Math.floor(scaled);
  const carry = Math.max(0, lower * 10 - 9);
  return digit + carry;
};

/** Frame indices in load order: every 8th, then every 4th, every 2nd, then the rest. */
export const getFrameLoadOrder = (
  frameCount: number,
  passes: readonly number[],
) => {
  const seen = new Set<number>();
  const order: number[] = [];
  for (const step of passes) {
    for (let index = 0; index < frameCount; index += step) {
      if (seen.has(index)) continue;
      seen.add(index);
      order.push(index);
    }
  }
  return order;
};

/** Nearest index in `available` to `target`, preferring the earlier frame on ties. */
export const findNearestFrame = (
  target: number,
  available: Iterable<number>,
): number | null => {
  let best: number | null = null;
  let bestDistance = Number.POSITIVE_INFINITY;
  for (const index of available) {
    const distance = Math.abs(index - target);
    if (
      distance < bestDistance ||
      (distance === bestDistance && best !== null && index < best)
    ) {
      best = index;
      bestDistance = distance;
    }
  }
  return best;
};
